import React, { useState, useEffect, useCallback } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { pmt2Service } from '@/services/pmt2Service'; // Ensure main service is imported
import { Timestamp } from 'firebase/firestore';

interface ClientNotesAsinDashboardProps {
  product: PMT2Product | null;
}

// Interface for the object *inside* the entries array
interface AsinNoteDetails {
    date: Timestamp;
    'Your Name': string;
    newValue?: string; // Note text might be here
    Note?: string;     // Or note text might be here
    // Add other potential fields from the entry object if needed
    [key: string]: any;
}

// Interface for the document fetched from Firestore
interface AsinNoteDocument {
  id: string; // Document ID (ASIN)
  entries?: AsinNoteDetails[]; // Array containing the actual note
  // Add other potential top-level fields if needed
  [key: string]: any;
}

/**
 * Client Notes ASIN Dashboard Component
 *
 * Displays the specific client note for the selected ASIN, if one exists.
 */
const ClientNotesAsinDashboard: React.FC<ClientNotesAsinDashboardProps> = ({ product }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [noteDocument, setNoteDocument] = useState<AsinNoteDocument | null>(null); // State for the fetched document

  // Memoize the fetch function
  const fetchAsinNote = useCallback(async (companyName: string, asin: string) => {
    setNoteDocument(null); // Reset on new fetch
    setError(null);
    setLoading(true);

    try {
      // Use the correct service function
      const entryDoc = await pmt2Service.getClientNoteForAsin(companyName, asin);

      // Only update state if the product hasn't changed during the fetch
      if (product && product.id === asin && product.companyName === companyName) {
        setNoteDocument(entryDoc); // Set the document object (or null)
      }
    } catch (err) {
      console.error("Error fetching ASIN client note:", err);
      setError('Failed to load ASIN note. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [product]); // Depend on product

  // Effect to fetch data when product changes
  useEffect(() => {
    if (product) {
      fetchAsinNote(product.companyName, product.id);
    } else {
      // Clear state if product becomes null
      setNoteDocument(null);
      setError(null);
      setLoading(false);
    }
  }, [product, fetchAsinNote]);

  // --- Date/Note Formatting Helpers (Similar to ASINReviewHistoryDashboard) ---
  const calculateDaysAgo = (timestamp: Timestamp | null): number => {
    if (!timestamp?.toDate) return 0;
    try {
      const entryDate = timestamp.toDate();
      entryDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffTime = today.getTime() - entryDate.getTime();
      return Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
    } catch { return 0; }
  };

  const formatDate = (timestamp: Timestamp | null): string => {
    if (!timestamp?.toDate) return 'Unknown date';
    try {
      return timestamp.toDate().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch { return 'Invalid date'; }
  };

  // Format notes specific to this dashboard - ONLY split by newline
  const formatAsinNote = (note: string): string[] => {
    if (!note) return [];
    let cleanedNote = note.trim();
    // Remove "^" suffix if present (though less likely here)
    const caretIndex = cleanedNote.indexOf('^');
    if (caretIndex !== -1) cleanedNote = cleanedNote.substring(0, caretIndex).trim();

    // ONLY split by explicit newline characters
    return cleanedNote.split('\n')
        .map(s => s.trim()) // Trim whitespace from each potential line
        .filter(Boolean); // Remove any empty strings
  };

   const renderWithLinks = (text: string): React.ReactNode => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return <a key={index} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{part}</a>;
      }
      return part;
    });
  };
  // --- End Helpers ---


  // --- Render Logic ---
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          <span className="ml-2">Loading ASIN note...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-md text-red-700 dark:text-red-300">
          {error}
        </div>
      );
    }

    // Extract the actual note data from the entries array
    const actualNoteData = noteDocument?.entries?.[0];

    if (!actualNoteData) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No client note found for this ASIN.
        </div>
      );
    }

    // Display the single note entry using actualNoteData
    const daysAgo = calculateDaysAgo(actualNoteData.date);
    // Combine 'New Value' and 'Note' fields, prioritizing 'New Value'
    const noteFromNewValue = actualNoteData['New Value'] || '';
    const noteFromNoteField = actualNoteData['Note'] || '';
    // Concatenate, adding a space only if both have content. Trim at the end.
    const combinedNote = `${noteFromNewValue}${noteFromNewValue && noteFromNoteField ? ' ' : ''}${noteFromNoteField}`.trim();

    const notes = formatAsinNote(combinedNote); // Format the combined note
    const formattedDate = formatDate(actualNoteData.date);

    return (
       <div className="space-y-4">
          <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700/50">
            <div className="flex flex-wrap gap-x-4 gap-y-1 items-center mb-2 text-sm">
              <span className="font-medium">{daysAgo} days ago</span>
              <span className="text-gray-600 dark:text-gray-400">{formattedDate}</span>
              <span className="text-gray-600 dark:text-gray-400">{actualNoteData['Your Name'] || 'Unknown'}</span>
            </div>
            {/* Always render as a list to get bullet points */}
            {notes.length > 0 ? (
              <ul className="list-disc pl-5 mt-1 space-y-1 text-sm">
                {notes.map((note, idx) => (
                  <li key={idx} className="text-gray-800 dark:text-gray-200">{renderWithLinks(note)}</li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 italic mt-2 text-sm">No note content provided</p>
            )}
          </div>
       </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
      <h2 className="text-2xl font-bold mb-6">Client Notes - ASIN</h2>
      {renderContent()}
    </div>
  );
};

export default ClientNotesAsinDashboard;